import { t } from 'i18next'

import { AgregadorParcelas } from '@/features/proposta/entities/agregador-parcelas'
import type { TSimulacaoProposta } from '@/features/proposta/schemas'
import { formatDateToLocale } from '@/utils/date.utils'

export interface ResumoSimulacao {
  nomeEmpreendimento: string
  blocoEmpreendimento: string
  dataMarcacaoAlvenaria: string
  valorProposta: string
  valorPoupanca: string
  valorSaldo: string
  entrada: string[]
  mensais: string[]
  intermediarias: string[]
  intermediariasPosConclusao: string[]
  posConclusao: string[]
  codigoProdutoPrincipal: string
  codigosAcessorios: string
  tipoDeProduto?: TSimulacaoProposta['produto']['tipoDoProduto']
  tipoDeAmortizacao?: string
  formaDePagamento?: string
  labelBloco: string
  labelProduto: string
  prazoEmMeses?: number
}

export class ResumoSimulacoes {
  static from(
    simulacaoUnidade?: TSimulacaoProposta | null,
    simulacaoLote?: TSimulacaoProposta | null,
    simulacoesAcessorios: TSimulacaoProposta[] = [],
  ): ResumoSimulacao {
    const simulacoes = [simulacaoUnidade, simulacaoLote, ...simulacoesAcessorios].filter(
      (simulacao): simulacao is TSimulacaoProposta => !!simulacao,
    )
    if (!simulacoes.length) return ResumoSimulacoes.emptyResumo()
    return ResumoSimulacoes.buildResumo(simulacoes)
  }

  private static buildResumo(simulacoes: TSimulacaoProposta[]): ResumoSimulacao {
    const primeiraSimulacao = simulacoes[0]!
    const { agregador, totalProposta, totalPoupanca, totalSaldo } = this.aggregateSimulacoes(simulacoes)

    const produtosPrincipais = new Set(['Unidade', 'Lote'])
    const simulacaoProdutoPrincipal = simulacoes.find((simulacao) =>
      produtosPrincipais.has(simulacao.produto.tipoDoProduto),
    )

    const codigoProdutoPrincipal = simulacaoProdutoPrincipal?.produto.codigo ?? '-'
    const acessorios = simulacoes.filter((s) => s.produto.tipoDoProduto === 'Acessorio').map((s) => s.produto.codigo)
    const codigosAcessorios = acessorios.length ? acessorios.join(', ') : '-'

    const tipoDeProduto = primeiraSimulacao.produto.tipoDoProduto
    const { labelBloco, labelProduto } = this.getLabels(tipoDeProduto)

    const simulacaoComData = simulacoes.find(
      (simulacao) => simulacao.produto.tipoDoProduto === 'Unidade' && simulacao.produto.dataMarcacaoAlvenaria,
    )
    const dataMarcacaoAlvenaria = simulacaoComData?.produto.dataMarcacaoAlvenaria
      ? formatDateToLocale(simulacaoComData.produto.dataMarcacaoAlvenaria)
      : '-'

    return {
      nomeEmpreendimento: primeiraSimulacao.empreendimento.nome,
      blocoEmpreendimento: primeiraSimulacao.bloco.nome,
      dataMarcacaoAlvenaria,
      valorProposta: this.formatMoney(totalProposta),
      valorPoupanca: this.formatMoney(totalPoupanca),
      valorSaldo: this.formatMoney(totalSaldo),
      entrada: agregador.getEntradas(),
      mensais: agregador.getMensais(),
      intermediarias: agregador.getIntermediarias(),
      intermediariasPosConclusao: agregador.getIntermediariasPosConclusao(),
      posConclusao: agregador.getPosConclusao(),
      codigoProdutoPrincipal,
      codigosAcessorios,
      tipoDeProduto,
      tipoDeAmortizacao: primeiraSimulacao.tipoDeAmortizacao ?? undefined,
      formaDePagamento: primeiraSimulacao.formaDePagamento ?? undefined,
      labelBloco,
      labelProduto,
      prazoEmMeses: primeiraSimulacao.prazoEmMeses ?? undefined,
    }
  }

  private static aggregateSimulacoes(simulacoes: TSimulacaoProposta[]): {
    agregador: AgregadorParcelas
    totalProposta: number
    totalPoupanca: number
    totalSaldo: number
  } {
    const agregador = new AgregadorParcelas()

    let totalProposta = 0
    let totalPoupanca = 0
    let totalSaldo = 0

    for (const simulacao of simulacoes) {
      totalProposta += simulacao.valorTotalSimulado ?? 0
      totalPoupanca += simulacao.valorPoupanca ?? 0
      totalSaldo += simulacao.valorSaldo ?? 0

      agregador.agregarEntradas(simulacao.entrada)
      agregador.agregarMensais(simulacao.mensais)
      agregador.agregarIntermediarias(simulacao.intermediarias)
      agregador.agregarIntermediariasPosConclusao(simulacao.intermediariasPosConclusao ?? [])
      agregador.agregarPosConclusao(simulacao.posConclusao)
    }

    return {
      agregador,
      totalProposta,
      totalPoupanca,
      totalSaldo,
    }
  }

  private static getLabels(tipoProduto: TSimulacaoProposta['produto']['tipoDoProduto']): {
    labelBloco: string
    labelProduto: string
  } {
    if (tipoProduto === 'Lote') {
      return {
        labelBloco: t('proposta:resumo_proposta.proposta_compra.label.quadra'),
        labelProduto: t('proposta:resumo_proposta.proposta_compra.label.lote'),
      }
    }

    return {
      labelBloco: t('proposta:resumo_proposta.proposta_compra.label.torre'),
      labelProduto: t('proposta:resumo_proposta.proposta_compra.label.unidade'),
    }
  }

  private static formatMoney(n: number): string {
    return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
  }

  private static emptyResumo(): ResumoSimulacao {
    return {
      nomeEmpreendimento: '-',
      blocoEmpreendimento: '-',
      dataMarcacaoAlvenaria: '-',
      valorProposta: '-',
      valorPoupanca: '-',
      valorSaldo: '-',
      entrada: [],
      mensais: [],
      intermediarias: [],
      intermediariasPosConclusao: [],
      posConclusao: [],
      codigoProdutoPrincipal: '-',
      codigosAcessorios: '-',
      tipoDeAmortizacao: undefined,
      formaDePagamento: undefined,
      labelBloco: 'Torre',
      labelProduto: 'Unidade',
      prazoEmMeses: undefined,
    }
  }
}
